import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17142 — the invoice headers of each past day, added up, for the reports
 * that read invoice totals rather than lines (the KPI cards, KPI Details,
 * Cumulative Sales vs Goal, the Sales Helper's sales total). About one row
 * per customer per day, where `sales_summary_line_day` holds about one per
 * invoice line.
 *
 * One row per day, customer, invoice rep, company, invoice type, cancelled
 * flag and has-sales flag, over the invoices that are not deleted. Values are
 * stored exactly as on the invoices (no credit-memo sign flip; cancelled
 * invoices kept as a column), so each report keeps its own rule for both.
 * Customers that are later deleted keep their rows; reports drop them at
 * query time. Only ids are stored.
 *
 * Every measure is a SUM of the invoice expression it names, NULL when every
 * invoice of the row has NULL there, so adding rows up again gives the same
 * figure as adding up the invoices. Built and refreshed with the line
 * summary, for the same days, by the sales summary queue.
 *
 * Not audited (`auditable: false`): a derived cache rebuilt a day at a time.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('sales_summary_invoice_day', {}, { auditable: false })
// The scans of the invoice-total reports: a date range, the two flags each
// report filters on, the company, then the customer and rep they filter and
// group by and the sums they read, so a year is read from the index alone.
@Index('sales_summary_invoice_day__date_sales', [
	'date',
	'isCancelled',
	'hasSales',
	'fkCompanyId',
	'fkClientId',
	'fkUserId',
	'salesSubtotal',
	'salesGrossProfit',
	'salesTotal',
	'invoiceCount',
])
// One customer's history, and the reports filtered to picked customers.
@Index('sales_summary_invoice_day__fk_client__id_date', ['fkClientId', 'date'])
export class SalesSummaryInvoiceDayEntity extends BaseEntity {
	/** The invoice date (`invoice.date`). */
	@Column('date', { name: 'date' })
	public date!: string;

	/** `invoice._fk_client__id`. */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** The rep the invoice is credited to (`invoice.fk_user__id`), NULL and 0 kept apart. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	/** `invoice.fk_company__id`. */
	@Column('int', { name: 'fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	/** `invoice.type` as stored (1 invoice, 2 credit memo); no sign is flipped. */
	@Column('varchar', { name: 'type', length: 10, nullable: true })
	public type!: string | null;

	/** 1 when the invoice is cancelled (`invoice.status` = '4'). */
	@Column({ type: 'boolean', name: 'isCancelled', width: 1, default: () => "'0'" })
	public isCancelled!: boolean;

	/**
	 * 1 when the invoice counts as a sales invoice: its total without its
	 * non-sales lines is not zero (`invoiceHasSalesSql`).
	 */
	@Column({ type: 'boolean', name: 'hasSales', width: 1, default: () => "'0'" })
	public hasSales!: boolean;

	/** SUM(subtotal - nonSalesSubtotal): the product sales the reports sum. */
	@Column('decimal', { name: 'salesSubtotal', nullable: true, precision: 23, scale: 2 })
	public salesSubtotal!: string | null;

	/** SUM(totalGP - nonSalesGP). */
	@Column('decimal', { name: 'salesGrossProfit', nullable: true, precision: 23, scale: 2 })
	public salesGrossProfit!: string | null;

	/** SUM(total - nonSalesSubtotal): the invoiced amount without non-sales lines. */
	@Column('decimal', { name: 'salesTotal', nullable: true, precision: 23, scale: 2 })
	public salesTotal!: string | null;

	/** SUM(subtotal), as stored. */
	@Column('decimal', { name: 'subtotal', nullable: true, precision: 23, scale: 2 })
	public subtotal!: string | null;

	/** SUM(total), as stored. */
	@Column('decimal', { name: 'total', nullable: true, precision: 23, scale: 2 })
	public total!: string | null;

	/** SUM(totalGP), as stored. */
	@Column('decimal', { name: 'grossProfit', nullable: true, precision: 23, scale: 2 })
	public grossProfit!: string | null;

	/** SUM(tax). */
	@Column('decimal', { name: 'tax', nullable: true, precision: 23, scale: 2 })
	public tax!: string | null;

	/** SUM(freight). */
	@Column('decimal', { name: 'freight', nullable: true, precision: 23, scale: 2 })
	public freight!: string | null;

	/** SUM(shipCost). */
	@Column('decimal', { name: 'shipCost', nullable: true, precision: 23, scale: 2 })
	public shipCost!: string | null;

	/** SUM(totalGPMargin), for an average with {@link totalGpMarginCount}. */
	@Column('decimal', {
		name: 'totalGpMarginSum',
		nullable: true,
		precision: 23,
		scale: 2,
	})
	public totalGpMarginSum!: string | null;

	/** COUNT(totalGPMargin): the invoices with a margin. */
	@Column('int', { name: 'totalGpMarginCount', unsigned: true })
	public totalGpMarginCount!: number;

	/**
	 * COUNT(subtotal): the invoices with a subtotal, so an average of the
	 * sales subtotal (which is NULL exactly when the subtotal is) is
	 * `salesSubtotal / subtotalCount`, as AVG() over the invoices would be.
	 */
	@Column('int', { name: 'subtotalCount', unsigned: true, default: 0 })
	public subtotalCount!: number;

	/** COUNT(shipCost): the invoices with a ship cost, for its average. */
	@Column('int', { name: 'shipCostCount', unsigned: true, default: 0 })
	public shipCostCount!: number;

	/** The number of invoices in the row. */
	@Column('int', { name: 'invoiceCount', unsigned: true })
	public invoiceCount!: number;

	/** The highest invoice id in the row (latest-invoice tie-break). */
	@Column('int', { name: 'maxInvoiceId', unsigned: true })
	public maxInvoiceId!: number;
}
